import React, { useEffect, useState } from 'react';
import { apiCall } from '../AuthContext';
import { Store, Loader2, CheckCircle2, XCircle, DollarSign, AlertCircle, Users as UsersIcon } from 'lucide-react';

export default function AdminResellers() {
  const [tab, setTab] = useState('applications');
  const [resellers, setResellers] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        apiCall('/api/admin/resellers', { method: 'GET' }),
        apiCall('/api/admin/reseller-commissions', { method: 'GET' }),
      ]);
      setResellers(r.data.resellers || []);
      setCommissions(c.data.commissions || []);
    } catch (e) {
      setStatus({ kind: 'error', msg: e.response?.data?.detail || 'Load failed' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (u) => {
    setBusyId(u);
    try { await apiCall(`/api/admin/resellers/${u}/approve`, { method: 'POST' }); await load(); }
    catch (e) { setStatus({ kind: 'error', msg: e.response?.data?.detail || 'Approve failed' }); }
    finally { setBusyId(null); }
  };
  const deny = async (u) => {
    setBusyId(u);
    try { await apiCall(`/api/admin/resellers/${u}/deny`, { method: 'POST' }); await load(); }
    catch (e) { setStatus({ kind: 'error', msg: e.response?.data?.detail || 'Deny failed' }); }
    finally { setBusyId(null); }
  };
  const markPaid = async (id) => {
    setBusyId(id);
    try { await apiCall(`/api/admin/reseller-commissions/${id}/mark-paid`, { method: 'POST' }); await load(); }
    catch (e) { setStatus({ kind: 'error', msg: e.response?.data?.detail || 'Mark-paid failed' }); }
    finally { setBusyId(null); }
  };

  const totalOwed = commissions.filter(c => c.status === 'owed').reduce((s, c) => s + (c.commission_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-resellers-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-bold text-[#0F172A] tracking-tight">Reseller Admin</h1>
        <p className="text-slate-500 mt-1">Manage reseller applications and commissions.</p>
      </div>
      {status && (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm border ${status.kind === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
          {status.kind === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}{status.msg}
        </div>
      )}
      <div className="flex items-center gap-2 border-b border-slate-200">
        <button onClick={() => setTab('applications')} className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === 'applications' ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-slate-500'}`} data-testid="admin-reseller-tab-applications">
          <UsersIcon className="w-4 h-4 inline mr-1.5" />Applications ({resellers.filter(r => r.status === 'pending').length})
        </button>
        <button onClick={() => setTab('commissions')} className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === 'commissions' ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-slate-500'}`} data-testid="admin-reseller-tab-commissions">
          <DollarSign className="w-4 h-4 inline mr-1.5" />Commissions · ${totalOwed.toFixed(2)} owed
        </button>
      </div>
      {loading ? <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> :
        tab === 'applications' ? (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-3 py-2 text-left">Username</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Paid refs</th><th className="px-3 py-2 text-left">Payout</th><th className="px-3 py-2 text-left">Audience</th><th className="px-3 py-2 text-left">Actions</th></tr>
              </thead>
              <tbody>
                {resellers.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No reseller applications yet.</td></tr>}
                {resellers.map(r => (
                  <tr key={r.username} className="border-t border-slate-100" data-testid={`reseller-row-${r.username}`}>
                    <td className="px-3 py-2 font-semibold">{r.username}{r.company_name && <p className="text-xs text-slate-400 font-normal">{r.company_name}</p>}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${r.status === 'approved' ? 'bg-green-100 text-green-700' : r.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{(r.status || '').toUpperCase()}</span>
                      {r.source === 'auto_promoted' && <p className="text-[10px] text-slate-400 mt-0.5">auto @ 10+</p>}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{r.paid_referrals}</td>
                    <td className="px-3 py-2"><p className="text-xs">{r.payout_method || '—'}</p><p className="text-xs text-slate-400 truncate max-w-xs">{r.payout_details}</p></td>
                    <td className="px-3 py-2"><p className="text-xs text-slate-600 line-clamp-2 max-w-xs">{r.audience || '—'}</p></td>
                    <td className="px-3 py-2">
                      {r.status === 'pending' && (
                        <div className="flex gap-1">
                          <button onClick={() => approve(r.username)} disabled={busyId === r.username} className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-green-100 hover:bg-green-200 text-green-700 text-xs font-semibold" data-testid={`approve-${r.username}`}>
                            {busyId === r.username ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Approve
                          </button>
                          <button onClick={() => deny(r.username)} disabled={busyId === r.username} className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-red-100 hover:bg-red-200 text-red-700 text-xs font-semibold">
                            <XCircle className="w-3 h-3" /> Deny
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-3 py-2 text-left">Reseller</th><th className="px-3 py-2 text-left">From referral</th><th className="px-3 py-2 text-left">Gross</th><th className="px-3 py-2 text-left">Commission</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Actions</th></tr>
              </thead>
              <tbody>
                {commissions.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No commissions recorded yet.</td></tr>}
                {commissions.map(c => (
                  <tr key={c.id} className="border-t border-slate-100" data-testid={`commission-row-${c.id}`}>
                    <td className="px-3 py-2 font-semibold">{c.reseller_username}</td>
                    <td className="px-3 py-2 text-slate-600">{c.referee_username}</td>
                    <td className="px-3 py-2 tabular-nums">${(c.gross_amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 tabular-nums font-bold text-green-700">${(c.commission_amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${c.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{(c.status || '').toUpperCase()}</span></td>
                    <td className="px-3 py-2">
                      {c.status === 'owed' && (
                        <button onClick={() => markPaid(c.id)} disabled={busyId === c.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-green-100 hover:bg-green-200 text-green-700 text-xs font-semibold" data-testid={`mark-paid-${c.id}`}>
                          {busyId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Mark paid
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
}
